import { accountNameFromAppLocation } from '../../../shared/app-host.js'
import { parseLiveSegments } from '../../../shared/embed.js'

/**
 * Parses hierarchical path segments from a catch-all Vue Router route.
 *
 * URL structure: /:application/:pathSegments+
 * On app.{account}.localhost the account comes from the hostname.
 *
 * The path after the application preserves the page trail. Record links append
 * `pageUniqueTitle/accessKey`. The homepage is not a segment, so:
 *   - 1 segment → page only (e.g. /app/forums)
 *   - even length → page/accessKey pairs
 *       /app/threadview/def456 → page=threadview, key=def456
 *   - odd length → a list page, then page/accessKey pairs
 *       /app/forums/threadview/def456 → page=threadview, key=def456
 * With 2 or more segments the current page is the second-to-last segment.
 * A path that starts with `embed` is an iframe address. The page and access
 * key are the segments after `embed/app`, `embed/page`, or `embed/view`.
 */

function segmentsOf (route) {
  const segments = route && route.params && route.params.pathSegments

  if (!segments || segments.length === 0) return []

  return Array.isArray(segments) ? segments : [segments]
}

function liveLocation (route) {
  return parseLiveSegments(segmentsOf(route))
}

function parsePathSegments (route) {
  const located = liveLocation(route)

  return {
    pageUniqueTitle: located.pageUniqueTitle,
    accessKey: located.accessKey
  }
}

function getAllPathSegments (route) {
  return liveLocation(route).segments
}

export const routeParamsMixin = {
  inject: {
    modalRoute: { from: 'modalRoute', default: null }
  },
  computed: {
    routeAccount () {
      const fromHost = typeof window === 'undefined'
        ? ''
        : accountNameFromAppLocation(window.location)

      return fromHost || (this.$route && this.$route.params && this.$route.params.account) || 'default'
    },
    routePageUniqueTitle () {
      if (this.modalRoute && this.modalRoute.active && this.modalRoute.pageUniqueTitle) {
        return this.modalRoute.pageUniqueTitle
      }

      const parsed = parsePathSegments(this.$route)
      return parsed.pageUniqueTitle
    },
    routeAccessKey () {
      if (this.modalRoute && this.modalRoute.active) {
        return this.modalRoute.accessKey || null
      }

      const parsed = parsePathSegments(this.$route)
      return parsed.accessKey
    },
    routeAllPathSegments () {
      if (this.modalRoute && this.modalRoute.active) {
        return this.modalRoute.segments || []
      }

      return getAllPathSegments(this.$route)
    },
    routeEmbed () {
      if (!this.$route) return null

      return liveLocation(this.$route).embed
    },
    routePath () {
      if (this.modalRoute && this.modalRoute.active && this.modalRoute.path) {
        return this.modalRoute.path
      }

      const located = this.$route ? liveLocation(this.$route) : null
      const application = this.$route && this.$route.params && this.$route.params.application

      if (located && located.embed && application) {

        const parts = [application].concat(located.segments || [])

        return `/${parts.map((part) => encodeURIComponent(part)).join('/')}`
      }

      return (this.$route && this.$route.path) || ''
    }
  }
}

export { parsePathSegments, getAllPathSegments }
